// Moved from `src/hooks/mutation/useMutateSendBatchTeamInvites.ts` (now the app
// delegate) with the org-teams D2 residue (ARCFE-1062). This was stayer 4 of the
// four OT4 left behind (ARCFE-1033); its D1 half — `sendBatchTeamInvitationsWithRoleIds`
// stranded in the uncarved `src/api/scribeAPIV2_1.ts` — closed at ARCFE-1041,
// which carved it into `../api/teams.ts` beside the three v2 invitation fns.
//
// Severed per ARCFE-921: the intl'd destructive toast and the Rematch
// `trackSendTeamInvitation` dispatch, which fires ONCE PER INVITEE and reads the
// per-email invite link out of the response — so the port hands the delegate the
// response body and the variables rather than a pre-chewed summary.
//
// `teamName` and `source` were hook PARAMETERS that existed solely to feed that
// dispatch. They do not cross: the delegate already holds them and closes over
// them, so this package's surface never learns an analytics vocabulary.
//
// COMMERCE `invalidateCurrentTeamBillingInfo(teamId)` crosses in the same port —
// per delivery section 3 the owning domain exports the command and app
// integration sequences it, never injected through `configureWorkspaceData`
// (design D7). The `GET_TEAM_DETAILS` invalidation it followed is workspace's
// own identity and stays here, ahead of the port, in the legacy order.
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GET_TEAM_DETAILS, type BatchTeamInvitationsResponse } from '../../contracts';
import { sendBatchTeamInvitationsWithRoleIds } from '../api/teams';
import { isTransportError, type WorkspaceHttpError } from '../config';

export interface SendBatchTeamInvitesVariables {
  teamId: string;
  emails: string[];
  roleIds?: string[];
}

export interface UseMutateSendBatchTeamInvitesOptions {
  /**
   * App-reaction port fired at the END of `onSuccess`, holding — in the legacy
   * order — the COMMERCE `invalidateCurrentTeamBillingInfo(teamId)` call and the
   * per-invitee Rematch dispatch. It receives the unwrapped response body (the
   * dispatch reads `success_emails[email]` for each invite link) and the
   * variables (`emails.length` is the dispatched `totalEmailCount`).
   */
  onInvitesSent?: (
    data: BatchTeamInvitationsResponse,
    variables: SendBatchTeamInvitesVariables
  ) => void;
  /** App-reaction port: fired where the legacy destructive toast fired. */
  onSendInvitesError?: (error: WorkspaceHttpError) => void;
}

/** POST /teams/{team_id}/invitations (v2.1 — batch, with role ids) */
export function useMutateSendBatchTeamInvites(
  options?: UseMutateSendBatchTeamInvitesOptions
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ teamId, emails, roleIds }: SendBatchTeamInvitesVariables) => {
      const data = await sendBatchTeamInvitationsWithRoleIds(teamId, emails, roleIds);
      return data;
    },
    onSuccess: ({ data }, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [GET_TEAM_DETAILS, { teamId: variables.teamId }],
      });
      options?.onInvitesSent?.(data, variables);
    },
    onError: (error) => {
      if (isTransportError(error)) {
        options?.onSendInvitesError?.(error);
      }
    },
  });
}
